import { useState } from 'react';

import { faEye } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components';

import {
  SortableTable,
  type TableColumn,
} from '../../common-components/composed/sortableTable/SortableTable';
import type { VoiceNoteTemplate } from '../../react-query/videa-notes/useVoiceNoteTemplates';
import { capitalize } from '../../utils/string';

import { TemplateDetailsDialog } from './TemplateDetailsDialog';

type SortDirection = 'asc' | 'desc';

interface TemplatesTableProps {
  templates: VoiceNoteTemplate[];
  sortField: string;
  sortDirection: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
  scrollContainerRef?: React.Ref<HTMLDivElement>;
  sentinelRef?: React.Ref<HTMLDivElement>;
}

export const TemplatesTable = ({
  templates,
  sortField,
  sortDirection,
  onSort,
  scrollContainerRef,
  sentinelRef,
}: TemplatesTableProps) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<VoiceNoteTemplate | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleViewTemplate = (template: VoiceNoteTemplate) => {
    setSelectedTemplate(template);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedTemplate(null);
  };

  const getCreatorDisplay = (template: VoiceNoteTemplate) => {
    if (!template.createdBy) {
      return <div className="vai-text-sm vai-text-gray-900">VideaAI</div>;
    }

    return (
      <div className="vai-flex vai-flex-col vai-gap-1">
        <div className="vai-text-sm vai-text-gray-900">
          {template.createdBy.name}
        </div>
        <div className="vai-text-xs vai-text-gray-500">
          {capitalize(template.createdBy.type)}
        </div>
      </div>
    );
  };

  const columns: TableColumn<VoiceNoteTemplate>[] = [
    {
      key: 'name',
      header: <Trans>Template Name</Trans>,
      width: 'vai-w-[300px]',
      isSortable: true,
      render: (template: VoiceNoteTemplate) => (
        <div className="vai-text-sm vai-font-medium vai-text-gray-900">
          {template.name}
        </div>
      ),
    },
    {
      key: 'creator',
      header: <Trans>Creator</Trans>,
      width: 'vai-w-[250px]',
      isSortable: true,
      render: (template: VoiceNoteTemplate) => getCreatorDisplay(template),
    },
    {
      key: 'type',
      header: <Trans>Type</Trans>,
      width: 'vai-w-[200px]',
      isSortable: true,
      render: (template: VoiceNoteTemplate) => (
        <div className="vai-text-sm vai-text-gray-600">
          {capitalize(template.type)}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <Trans>Actions</Trans>,
      width: 'vai-w-[150px]',
      isSortable: false,
      render: (template: VoiceNoteTemplate) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleViewTemplate(template)}
          className="vai-flex vai-items-center vai-gap-2"
        >
          <FontAwesomeIcon icon={faEye} />
          <Trans>View</Trans>
        </Button>
      ),
    },
  ];

  return (
    <>
      <SortableTable
        data={templates}
        columns={columns}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        scrollRef={scrollContainerRef}
        sentinelRef={sentinelRef}
      />

      {selectedTemplate && (
        <TemplateDetailsDialog
          template={selectedTemplate}
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
};
